import React from 'react';
import MapComponentLibrary from './MapComponentLibrary';
import { cloneDeep, isEqual } from 'lodash';
import MapDrawingZone from './MapDrawingZone';
import MapPropertyPanel from './MapPropertyPanel';
import { v4 as uuidv4 } from 'uuid';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import './map-editor.scss';
import sensorsActions from '../../../../../actions/sensors.actions';
import { getSensorBgColor, getSquareBgColor } from './Helpers/func';

const MapEditor = React.forwardRef(({ sensors, groups, ...props }, ref) => {
  const [bgFile, setBgFile] = React.useState(null);
  const [bg, setBg] = React.useState(props.bg);
  const [originalDimension, setOriginalDimension] = React.useState(null);
  const [shapeArr, setShapeArr] = React.useState(
    props.isEdit ? props.shapes : [],
  );
  const [selectedShape, setSelectedShape] = React.useState(null);
  const [editorValid, setEditorValid] = React.useState(true);
  React.useEffect(() => {
    if (props.shapes && props.shapes.length) {
      setShapeArr(props.shapes);
    }
    if (props.originalDimension) {
      setOriginalDimension(props.originalDimension);
    }
  }, [props.shapes, props.originalDimension]);
  function onDragShape(id, left, top) {
    const index = shapeArr.findIndex((shape) => shape.id === id);
    const _shapes = cloneDeep(shapeArr);
    _shapes[index].position = {
      left,
      top,
    };
    setSelectedShape(_shapes[index]);
    setShapeArr(_shapes);
  }
  function onAddShape(shapeType) {
    const id = `shape${uuidv4()}`;
    const maxZindex = shapeArr.length
      ? Math.max.apply(
          Math,
          shapeArr.map(function (obj) {
            return obj.zIndex;
          }),
        )
      : -1;
    let shape = {
      id,
      zIndex: maxZindex + 1,
      position: { left: 0, top: 0 },
      dimension: { width: 100, height: 100 },
      borderSize: 1,
      borderStyle: 'solid',
      borderColor: 'black',
    };
    switch (shapeType) {
      case 1:
        shape.className = 'Multi';
        shape.name = `Sensor ${maxZindex + 1}`;
        shape.tagBoxSize = 50;
        shape.tagLocation = 'None';
        shape.tagFontSize = 12;
        shape.tagFontName = 'Arial';
        shape.tagTextColor = 'black';
        shape.tagBackGroundColor = 'white';
        shape.valueFontSize = 12;
        shape.valueTextColor = 'black';
        shape.valueFontName = 'Arial';
        shape.valueBackGroundColor = 'alarm';
        shape.sensorId = undefined;
        break;
      case 2:
        shape.className = 'SquareShape';
        shape.name = `Group ${maxZindex + 1}`;
        shape.valueBackGroundColor = 'white';
        shape.groupId = undefined;
        break;
      case 4:
        shape.className = 'Chart';
        shape.name = `Chart ${maxZindex + 1}`;
        shape.tagBoxSize = 50;
        shape.tagLocation = 'None';
        shape.tagFontSize = 12;
        shape.tagFontName = 'Arial';
        shape.tagTextColor = 'black';
        shape.tagBackGroundColor = 'white';
        shape.dimension = { width: 450, height: 300 };
        shape.backgroundColor = 'white';
        shape.timeRange = 1;
        break;
      default:
        shape.className = 'CircleShape';
        shape.name = `Sensor (Simple) ${maxZindex + 1}`;
        shape.borderRadius = '50%';
        shape.tagBoxSize = 50;
        shape.tagLocation = 'None';
        shape.tagFontSize = 12;
        shape.tagFontName = 'Arial';
        shape.tagTextColor = 'black';
        shape.tagBackGroundColor = 'white';
        shape.valueBackGroundColor = 'alarm';
        shape.alarmBackgroundColor = 'transparent';
        break;
    }
    setShapeArr((shapeArr) => [...shapeArr, shape]);
    setSelectedShape(shape);
  }
  function gcd(a, b) {
    return b === 0 ? a : gcd(b, a % b);
  }
  function onUploadBg(e) {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      function () {
        let src = reader.result;
        let image = new Image();
        image.src = src;
        image.onload = function () {
          setBgFile(file);
          setBg({
            className: 'Background',
            image,
            fileName: file.name,
            fileType: file.type,
            position: { left: 0, top: 0 },
            dimension: { width: this.width, height: this.height },
          });
          setOriginalDimension({
            width: this.width,
            height: this.height,
            ratio: gcd(this.width, this.height),
          });
        };
      },
      false,
    );
    if (file) {
      reader.readAsDataURL(file);
    }
  }
  React.useEffect(() => {
    setSelectedShape(bg);
  }, [bg]);
  function onSelectShape(shape) {
    setSelectedShape(shape);
  }
  function onRemoveShape(shape) {
    if (selectedShape && selectedShape.id === shape.id) {
      setSelectedShape(null);
    }
    setShapeArr((shapeArr) => shapeArr.filter((obj) => obj.id !== shape.id));
  }
  async function onEdit(values, isError) {
    if (values.className === 'Background') {
      setBg(values);
    } else {
      let _shapeArr = cloneDeep(shapeArr);
      let editingShapeIndex = _shapeArr.findIndex(
        (shape) => shape.id === values.id,
      );

      if (!isEqual(_shapeArr[editingShapeIndex], values)) {
        let alarmBackgroundColor =
          _shapeArr[editingShapeIndex].alarmBackgroundColor;
        let alarmValue = _shapeArr[editingShapeIndex].alarmValue;
        if (
          ((values.className === 'Multi' &&
            values.valueBackGroundColor === 'alarm') ||
            values.className.match(/CircleShape|Circle/g)) &&
          values.sensorId !== undefined &&
          values.sensorId !== 0 &&
          _shapeArr[editingShapeIndex].sensorId !== values.sensorId
        ) {
          await sensorsActions
            .getAlarmStatusV2(values.sensorId)
            .then((resp) => {
              alarmBackgroundColor = getSensorBgColor(resp[0].alarmPriority);
              alarmValue = `${resp[0].textValue} ${resp[0].units}`;
            });
        }
        if (
          values.className.match(/SquareShape|Square/g) &&
          values.groupId !== undefined &&
          _shapeArr[editingShapeIndex].groupId !== values.groupId
        ) {
          await sensorsActions
            .getAlarmStatusByGroupV2(values.groupId)
            .then((resp) => {
              alarmBackgroundColor = getSquareBgColor(resp);
              alarmValue = `${resp[0].textValue} ${resp[0].units}`;
            });
        }
        _shapeArr[editingShapeIndex] = {
          ...values,
          alarmBackgroundColor,
          alarmValue,
        };
        setShapeArr(_shapeArr);
      }
    }
  }
  function onReorderShapes(shapes) {
    shapes.forEach((shape, index) => {
      shape.zIndex = index;
    });
    setShapeArr(shapes);
  }

  React.useImperativeHandle(ref, () => ({
    checkError() {
      return editorValid;
    },
    fetchShapes: () => {
      const multis = [];
      const squares = [];
      const circles = [];
      const charts = [];
      const _shapeArr = cloneDeep(shapeArr);
      _shapeArr.forEach((shape) => {
        if (shape.className === 'Multi') {
          const _shape = {
            ...shape,
            width: shape.dimension.width,
            height: shape.dimension.height,
            x: shape.position.left,
            y: shape.position.top,
            className: 'Multi',
            classType: 'MultiValue1',
            index: shape.zIndex,
          };
          delete _shape['position'];
          delete _shape['dimension'];
          multis.push(_shape);
        }
        if (shape.className === 'Square' || shape.className === 'SquareShape') {
          const _shape = {
            ...shape,
            width: shape.dimension.width,
            height: shape.dimension.height,
            x: shape.position.left,
            y: shape.position.top,
            className: 'SquareShape',
            classType: 'GroupAlertStatus',
            index: shape.zIndex,
          };
          delete _shape['position'];
          delete _shape['dimension'];
          squares.push(_shape);
        }
        if (shape.className === 'Circle' || shape.className === 'CircleShape') {
          const _shape = {
            ...shape,
            width: shape.dimension.width,
            height: shape.dimension.height,
            x: shape.position.left,
            y: shape.position.top,
            className: 'CircleShape',
            classType: 'CircleValue1',
            index: shape.zIndex,
          };
          delete _shape['position'];
          delete _shape['dimension'];
          circles.push(_shape);
        }
        if (shape.className === 'Chart' || shape.className === 'ChartShape') {
          const _shape = {
            ...shape,
            width: shape.dimension.width,
            height: shape.dimension.height,
            x: shape.position.left,
            y: shape.position.top,
            className: 'Chart',
            classType: 'ChartValue1',
            index: shape.zIndex,
          };
          delete _shape['position'];
          delete _shape['dimension'];
          charts.push(_shape);
        }
      });
      const shapes = {
        background: {
          width: bg && bg.dimension ? bg.dimension.width : 0,
          height: bg && bg.dimension ? bg.dimension.height : 0,
        },
        multis,
        squares,
        circles,
        charts,
      };

      return { shapes, bgFile };
    },
  }));
  return (
    <div className="d-flex flex-column" style={{ height: '100%' }}>
      <div className="map-editor-container">
        <MapComponentLibrary
          onAddShape={onAddShape}
          onUploadBg={onUploadBg}
          shapeArr={shapeArr}
          selectedShape={selectedShape}
          onSelectShape={onSelectShape}
          onRemoveShape={onRemoveShape}
          onReorderShapes={onReorderShapes}
        />
        <DndProvider backend={HTML5Backend}>
          <MapDrawingZone
            shapeArr={shapeArr}
            onSelectShape={onSelectShape}
            onSelectBg={() => setSelectedShape(bg)}
            bg={bg}
            selectedShape={selectedShape}
            onDragShape={onDragShape}
          />
        </DndProvider>

        {selectedShape ? (
          <MapPropertyPanel
            // formik={props.formik}
            groups={groups}
            sensors={sensors}
            shape={selectedShape}
            originalDimension={originalDimension}
            onEdit={onEdit}
            isValidated={(isErr) => {
              //
              if (Object.keys(isErr).length) {
                setEditorValid(false);
              } else {
                setEditorValid(true);
              }
            }}
            onDeselectShape={() => setSelectedShape(null)}
          />
        ) : null}
      </div>
    </div>
  );
});

export default MapEditor;
